import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONNECT_CHANNELS, CHANNEL_UNAVAILABLE_MSG, rc } from "@/lib/connect";
import { logEvent } from "@/lib/analytics";
import { toast } from "sonner";
import {
  WhatsappLogo, EnvelopeSimple, ChatText, PhoneCall, LockSimple, MagnifyingGlass, ArrowLeft,
  CheckCircle, XCircle, WarningCircle, Clock, Eye,
} from "@phosphor-icons/react";

const CH = {
  whatsapp: { label: "WhatsApp", Icon: WhatsappLogo },
  email: { label: "Email", Icon: EnvelopeSimple },
  sms: { label: "SMS", Icon: ChatText },
  voice: { label: "Voice", Icon: PhoneCall },
};
const uuid = () => "cmp-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export function ComposerDialog({ open, onOpenChange, account = null, readyChannels = [] }) {
  const [selected, setSelected] = useState(account);
  const [channel, setChannel] = useState("email");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [ctx, setCtx] = useState(null);
  const [preview, setPreview] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [warn, setWarn] = useState(null);
  const [idem] = useState(uuid());

  const ready = readyChannels.includes(channel);
  const emailChannel = channel === "email";
  const recipient = selected?.email || "";

  useEffect(() => {
    if (open) {
      setSelected(account || null);
      setChannel(readyChannels.includes("email") ? "email" : "whatsapp");
      setQuery(""); setResults([]); setTemplateId(""); setSubject(""); setBodyHtml("");
      setCtx(null); setPreview(null); setEmailInput(""); setExhausted(false); setBlocked(null); setWarn(null);
    }
  }, [open, account]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    try { const { data } = await api.get("/accounts", { params: { q, limit: 8 } }); setResults(data?.items || []); }
    catch { setResults([]); }
  }, []);
  useEffect(() => { const t = setTimeout(() => search(query), 250); return () => clearTimeout(t); }, [query, search]);

  // Load tenant Studio templates when an account is chosen.
  useEffect(() => {
    if (open && emailChannel && selected?.account_id) {
      api.get("/connect/email/studio/templates", { params: { status: "active" } })
        .then((r) => setTemplates(r.data?.templates || [])).catch(() => setTemplates([]));
    }
  }, [open, emailChannel, selected]);

  // Load rule status + history whenever account/template changes.
  const loadContext = useCallback(async () => {
    if (!selected?.account_id) return;
    try {
      const { data } = await api.get("/connect/email/context", {
        params: { account_id: selected.account_id, template_id: templateId || undefined },
      });
      setCtx(data);
    } catch { setCtx(null); }
  }, [selected, templateId]);
  useEffect(() => { if (open && emailChannel && selected?.account_id) loadContext(); }, [open, emailChannel, selected, templateId, loadContext]);

  const applyTemplate = (id) => {
    setTemplateId(id); setPreview(null);
    const t = templates.find((x) => x.id === id);
    if (t) { setSubject(t.subject || ""); setBodyHtml(t.html || ""); }
  };

  const addEmail = async () => {
    setAddingEmail(true);
    try {
      const { data } = await api.post("/connect/email/set-recipient", { account_id: selected.account_id, email: emailInput.trim() });
      setSelected((p) => ({ ...p, email: data.email }));
      toast.success("Email saved to this customer.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save email"); }
    finally { setAddingEmail(false); }
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post("/connect/email/studio/preview", {
        subject, html: bodyHtml, account_id: selected.account_id,
        category: templates.find((t) => t.id === templateId)?.category || null,
      });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const decision = ctx?.decision;
  const canSend = emailChannel && ready && selected && recipient && subject.trim() && bodyHtml.trim() && !sending
    && decision && decision.decision !== "BLOCK" && decision.decision !== "SCHEDULE";

  const doSend = async (allowDuplicate = false) => {
    setSending(true);
    try {
      await api.post("/connect/email/send", {
        account_id: selected.account_id, subject: subject.trim(), body: bodyHtml,
        template_id: templateId || null, allow_duplicate: allowDuplicate, idempotency_key: idem,
      });
      logEvent("connect_email_sent", { template: templateId || "custom" });
      toast.success("Email sent — it will appear in the timeline as delivery updates arrive.");
      window.dispatchEvent(new Event("recoveros:comms-changed"));
      window.dispatchEvent(new Event("recoveros:usage-changed"));
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      const msg = typeof d === "object" ? d?.message : d;
      const dec = typeof d === "object" ? d?.decision : null;
      if (e?.response?.status === 402 || code === "FREE_BALANCE_EXHAUSTED") { setExhausted(true); }
      else if (dec === "WARN") { setWarn({ message: msg }); }
      else if (dec === "BLOCK" || dec === "SCHEDULE") { setBlocked({ message: msg, next: d?.next_allowed_at }); }
      else { toast.error(msg || "Could not send the email. Please try again."); }
    } finally { setSending(false); }
  };

  const goRecharge = () => { onOpenChange(false); logEvent("connect_recharge_clicked", { from: "composer" }); window.location.assign("/subscription#recharge-rc"); };

  const RuleRow = ({ ok, warnState, label }) => (
    <div className="flex items-center gap-1.5 text-xs">
      {warnState ? <WarningCircle size={14} weight="fill" className="text-amber-500" />
        : ok ? <CheckCircle size={14} weight="fill" className="text-emerald-500" />
        : <XCircle size={14} weight="fill" className="text-red-500" />}
      <span className={ok ? "text-slate-700" : "text-red-600 font-medium"}>{label}</span>
    </div>
  );

  const dc = decision?.context || {};
  const isBlocked = decision && (decision.decision === "BLOCK" || decision.decision === "SCHEDULE");
  const isWarn = decision && decision.decision === "WARN";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="connect-composer">
        <DialogHeader>
          <DialogTitle>Start a Follow-up</DialogTitle>
          <DialogDescription>Who are you emailing, what will they receive, and can you send right now?</DialogDescription>
        </DialogHeader>

        {exhausted ? (
          <div data-testid="composer-exhausted" className="py-2">
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-center">
              <LockSimple size={26} weight="bold" className="text-amber-700 mx-auto mb-2" />
              <div className="text-sm font-bold text-amber-900">Your free communication balance has been used.</div>
              <p className="text-xs text-amber-800 mt-1">Recharge your RecoverOS Credits to continue sending.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Account */}
            <div>
              <Label className="text-xs">Customer / Account</Label>
              {selected ? (
                <div className="mt-1 flex items-center justify-between gap-2 border border-border rounded-sm px-3 py-2 bg-secondary/40" data-testid="composer-selected-account">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{selected.borrower_name}</div>
                    <div className="text-[11px] text-muted-foreground mono truncate">{selected.loan_id}</div>
                  </div>
                  <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => { setSelected(null); setCtx(null); }} data-testid="composer-change-account">Change</Button>
                </div>
              ) : (
                <div className="mt-1">
                  <div className="relative">
                    <MagnifyingGlass size={15} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, loan ID or mobile" className="rounded-sm pl-8" data-testid="composer-account-search" />
                  </div>
                  {results.length > 0 && (
                    <div className="mt-1 border border-border rounded-sm divide-y max-h-44 overflow-y-auto">
                      {results.map((a) => (
                        <button key={a.account_id} onClick={() => { setSelected(a); setResults([]); setQuery(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-secondary/60" data-testid={`composer-account-option-${a.account_id}`}>
                          <div className="text-sm font-medium truncate">{a.borrower_name}</div>
                          <div className="text-[11px] text-muted-foreground mono">{a.loan_id}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Channel */}
            <div>
              <Label className="text-xs">Channel</Label>
              <div className="mt-1 grid grid-cols-4 gap-2">
                {CONNECT_CHANNELS.map((c) => {
                  const M = CH[c]; const I = M.Icon; const isSel = channel === c; const isReady = readyChannels.includes(c);
                  return (
                    <button key={c} onClick={() => setChannel(c)} data-testid={`composer-channel-${c}`}
                      className={`relative flex flex-col items-center gap-1 py-2 rounded-sm border text-xs font-semibold transition-colors ${isSel ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-secondary/60"}`}>
                      <I size={18} weight="bold" />{M.label}
                      {isReady && <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {!emailChannel || !ready ? (
              <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2" data-testid="composer-unavailable">
                <LockSimple size={16} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900"><span className="font-semibold">{CH[channel].label} isn’t active yet.</span> {CHANNEL_UNAVAILABLE_MSG[channel]}</div>
              </div>
            ) : selected ? (
              <>
                {/* Recipient (WHO) */}
                {recipient ? (
                  <div>
                    <Label className="text-xs">Recipient</Label>
                    <Input value={recipient} readOnly className="rounded-sm mt-1 bg-secondary/30" data-testid="composer-recipient" />
                  </div>
                ) : (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 p-2.5" data-testid="composer-no-email">
                    <div className="text-xs text-amber-900 mb-1.5 font-medium">This customer has no email yet. Add one to send:</div>
                    <div className="flex gap-2">
                      <Input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="customer@email.com" className="rounded-sm bg-white" data-testid="composer-email-input" />
                      <Button size="sm" className="rounded-sm" disabled={addingEmail || !emailInput.trim()} onClick={addEmail} data-testid="composer-add-email">{addingEmail ? "Saving…" : "Add"}</Button>
                    </div>
                  </div>
                )}

                {/* Template (WHAT) */}
                <div>
                  <Label className="text-xs">Template</Label>
                  <Select value={templateId} onValueChange={applyTemplate}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="composer-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`composer-template-${t.id}`}>{t.name}{t.is_default ? " ★" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm mt-1" data-testid="composer-subject" />
                </div>
                {templateId ? (
                  <div className="text-[11px] text-muted-foreground">Body & variables are provided by the template. Use <span className="font-semibold">Preview</span> to see the final email.</div>
                ) : (
                  <div className="text-[11px] text-muted-foreground">Pick a template above to auto-fill a branded, variable-ready body.</div>
                )}

                {/* Rule status (WHEN / HOW OFTEN / CAN I SEND) */}
                {ctx && decision && (
                  <div className={`rounded-sm border p-3 ${isBlocked ? "border-red-200 bg-red-50" : isWarn ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="composer-rule-status">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      <RuleRow ok={decision.reason_code !== "OUTSIDE_HOURS" && decision.reason_code !== "BLOCKED_DAY"} label="Within sending hours" />
                      <RuleRow ok={!String(decision.reason_code || "").startsWith("FREQUENCY")} label={`Frequency: ${dc.used_day ?? 0}/${dc.cap_day ?? "-"} today`} />
                      <RuleRow ok={decision.reason_code !== "MINIMUM_GAP"} label={`Min gap: ${dc.min_gap_hours ?? "-"}h`} />
                      <RuleRow ok={decision.reason_code !== "DUPLICATE_RECENT"} warnState={decision.reason_code === "DUPLICATE_RECENT"} label="Not a recent duplicate" />
                    </div>
                    <div className="mt-2 pt-2 border-t border-black/5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-slate-600">
                      <div>Last email: {dc.last_email_at ? fmtTime(dc.last_email_at) : "—"}</div>
                      <div>Last 7 days: {dc.used_7d ?? 0}/{dc.cap_7d ?? "-"}</div>
                      {dc.exempt && <div className="col-span-2 text-emerald-700 font-medium">Transactional template — exempt from frequency/time rules.</div>}
                    </div>
                    {isBlocked && (
                      <div className="mt-2 text-xs font-medium text-red-700 flex items-start gap-1.5" data-testid="composer-blocked-inline">
                        <Clock size={14} weight="bold" className="mt-0.5 shrink-0" />
                        <span>{decision.message}{decision.next_allowed_at ? ` Next allowed: ${fmtTime(decision.next_allowed_at)}.` : ""}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Preview */}
                {preview && (
                  <div className="border border-border rounded-sm bg-slate-100 p-2" data-testid="composer-preview">
                    <div className="text-[11px] mb-1"><span className="text-muted-foreground">From:</span> {preview.sending_as}</div>
                    <iframe title="composer-preview" srcDoc={preview.html} className="w-full h-64 border-0 bg-white" data-testid="composer-preview-iframe" />
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-muted-foreground py-2">Select a customer to start.</div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          {exhausted ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setExhausted(false)} data-testid="composer-exhausted-back"><ArrowLeft size={14} weight="bold" className="mr-1" /> Back</Button>
              <Button className="rounded-sm" onClick={goRecharge} data-testid="composer-recharge">Recharge Credits</Button>
            </>
          ) : warn ? (
            <>
              <div className="text-xs text-amber-700 mr-auto flex items-center gap-1.5"><WarningCircle size={15} weight="fill" />{warn.message}</div>
              <Button variant="outline" className="rounded-sm" onClick={() => setWarn(null)} data-testid="composer-warn-cancel">Cancel</Button>
              <Button className="rounded-sm" onClick={() => { setWarn(null); doSend(true); }} data-testid="composer-warn-send">Send anyway</Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="composer-cancel">Close</Button>
              {emailChannel && ready && selected && recipient && (
                <Button variant="outline" className="rounded-sm" disabled={!subject.trim() || !bodyHtml.trim()} onClick={doPreview} data-testid="composer-preview-btn"><Eye size={14} weight="bold" className="mr-1.5" />Preview</Button>
              )}
              <Button className="rounded-sm" disabled={!canSend} onClick={() => doSend(false)} data-testid="composer-send">
                {sending ? "Sending…" : isBlocked ? "Blocked" : "Send email"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
